import { useState } from 'react';
import { Link } from 'react-router';

import Avatar from '@/auth/components/Avatar';

import { formatRelative } from '../components/EngagementHub/format';
import type { ViewerRole } from '../components/EngagementHub/roles';
import { canApproveEngagementLive, viewerSeesExpertRate } from '../components/EngagementHub/roles';
import type { ExpandedMode } from '../components/GovernanceApproverInbox/ApproveActions';
import { TONE_CLASSES } from '../components/shared/EngagementStatusBadge';
import GovernanceDecisionControls from './GovernanceDecisionControls';
import Section from './Section';
import { activityCopy } from './activityCopy';
import { hubBanner } from './banners';
import { formatEngagementMoney, formatHours } from './money';
import { ENGAGEMENT_HUB } from './operations';
import { engagementRoster, partyNamer } from './parties';
import { useSelfIdentity } from './session';
import type { EngagementView } from './viewModel';

interface OverviewPanelProps {
  view: EngagementView;
  viewerRole: ViewerRole;
  viewerUserId: string;
  now: Date;
}

export default function OverviewPanel({
  view,
  viewerRole,
  viewerUserId,
  now,
}: OverviewPanelProps): JSX.Element {
  const banner = hubBanner(view, viewerRole, viewerUserId);
  // The same roster gate `hubBanner` titles the banner with, so the frame that
  // says "Your approval is needed." and the controls cannot disagree.
  const canDecide = canApproveEngagementLive(
    view.status,
    view.awaiting,
    view.participants,
    viewerUserId
  );
  const [decisionMode, setDecisionMode] = useState<ExpandedMode | null>(null);
  // No roster, on purpose — the missing third argument is the decision, not an
  // oversight. `view.messageChannels[].members` is right there, but a roster is
  // PER CHANNEL and this panel is cross-channel: an `engagement.channel_joined`
  // row carries no channel_type (`EngagementActivityEvent` declares no payload),
  // so no single roster belongs to it. Passing the union would name an OF staff
  // member on a row that may be about the channel this reader cannot see, and
  // the same `nameOf` labels the Participants list below, where a roster hit
  // would read "· OnFrontiers" against a customer-side approver who simply is
  // not on the project. So `parties.ts:30` stays unreachable here and staff read
  // as "A participant" — pinned in roster-reveal.test.tsx, since a future caller
  // wiring the roster in would otherwise get no warning.
  const self = useSelfIdentity();
  const nameOf = partyNamer(view, viewerUserId, [], self);
  const roster = engagementRoster(view);

  return (
    <div className="flex flex-col gap-24">
      {/*
       * Frame, spacing and `role="status"` match the prototype's `Banner`
       * (`features/consulting-engagements/components/EngagementHub/Header.tsx:52`)
       * so the shipped Hub and the design story read as one component.
       *
       * The body colour deliberately does NOT match. The prototype renders it
       * as `${c.text} opacity-90`, which on the `muted` tone is
       * --onSurfaceMuted #6b7280 over --surfaceSecondary #f3f4f6 — about 3.6:1,
       * under WCAG AA, and still 4.4:1 with the opacity removed. That tone
       * carries real content, not decoration: the expert's decline note on a
       * canceled engagement and `closureBody` on a closed one. `text-on-surface`
       * keeps those readable.
       *
       * The governance approver decides inside the frame, under the banner
       * that asks them to (ENG-5752), because the terms the decision turns on
       * are on this tab. `role="status"` covers the text alone: a live region
       * is for announcements, and buttons and inputs do not belong in one.
       *
       * After a decision the Hub stays put. The mutation's `status`, `awaiting`
       * and `governanceFeedback` move the header chip and this banner through
       * the normalized cache at once; `useGovernanceDecision` then re-reads
       * ENGAGEMENT_HUB for the activity row. The same re-read settles a stale
       * Hub, where someone else acted first.
       */}
      {banner ? (
        <div
          className={`flex flex-col gap-12 rounded-md border px-16 py-12 ${TONE_CLASSES[banner.tone].bg} ${TONE_CLASSES[banner.tone].border}`}
        >
          <div className="flex flex-col gap-4" role="status">
            <span className={`subtitle-1 ${TONE_CLASSES[banner.tone].text}`}>{banner.title}</span>
            {banner.body ? <span className="text-on-surface body-2">{banner.body}</span> : null}
          </div>
          {canDecide ? (
            <div className="flex flex-col gap-12">
              <GovernanceDecisionControls
                engagementId={view.id}
                currency={view.currency}
                expandedMode={decisionMode}
                onExpand={setDecisionMode}
                refetchQueries={[ENGAGEMENT_HUB]}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      <Section title="Terms">
        <dl className="grid grid-cols-1 gap-16 sm:grid-cols-2">
          <Term label="Not-to-exceed hours">
            {view.nteHoursTotal === null
              ? 'Set at the offer step'
              : formatHours(view.nteHoursTotal)}
          </Term>
          <Term label="Hours confirmed">{formatHours(view.nteHoursConsumed)}</Term>
          {/*
           * §6.3 rate segregation, enforced by what the server sent: the expert
           * gets their own rate, the customer side gets the loaded rate, and
           * whichever side the viewer isn't on arrived as null and renders no
           * row at all — never a zero, never a placeholder.
           */}
          {viewerSeesExpertRate(viewerRole) && view.expertRateCents !== null ? (
            <Term label="Your rate">
              {formatEngagementMoney(view.expertRateCents, view.currency)} / hour
            </Term>
          ) : null}
          {/*
           * "Blended", not "Rate" (ENG-5731, the second half of FB-174). This
           * figure is the not-to-exceed total divided by the hours, so on a
           * tiered account it is an average that no individual hour is billed
           * at — a 7-hour engagement on a 5/15 account reads $857.14 when the
           * hours themselves were priced at $1,000 and $500. Labelling it is
           * all this ticket does; showing the bands it averages is ENG-5718's
           * job on the Order and Billing surfaces, against the pricing model
           * ENG-5656 replaces.
           *
           * The expert's own row above is NOT blended — it is their single
           * agreed rate — which is why only this branch is relabelled.
           */}
          {!viewerSeesExpertRate(viewerRole) && view.customerRateLoadedCents !== null ? (
            <Term label="Blended rate">
              {formatEngagementMoney(view.customerRateLoadedCents, view.currency)} / hour
            </Term>
          ) : null}
          {!viewerSeesExpertRate(viewerRole) && view.nteTotalCents !== null ? (
            <Term label="Not-to-exceed total">
              {formatEngagementMoney(view.nteTotalCents, view.currency)}
            </Term>
          ) : null}
        </dl>
      </Section>

      {/*
       * The customer's ask and the expert's scope of work are separate records —
       * the offer is what the expert committed to, and showing it next to the
       * request is how a drift between them becomes visible. Neither is
       * viewer-gated.
       */}
      <Section title="Scope">
        {view.requestScopeDescription === null && view.offerScopeDescription === null ? (
          <p className="text-on-surface-muted body-2">No scope has been recorded yet.</p>
        ) : (
          <dl className="flex flex-col gap-16">
            {view.requestScopeDescription !== null ? (
              <Term label="Requested">{view.requestScopeDescription}</Term>
            ) : null}
            {view.offerScopeDescription !== null ? (
              <Term label="Scope of work">{view.offerScopeDescription}</Term>
            ) : null}
          </dl>
        )}
      </Section>

      <Section title="Participants">
        {roster === null ? (
          // Null roster = the viewer isn't entitled to it (the expert never
          // sees the customer's approver chain). Not an error state, and the
          // entitlement check lives in `engagementRoster` so this panel cannot
          // disagree with it.
          <p className="text-on-surface-muted body-2">
            Participant details are visible to the customer side only.
          </p>
        ) : roster.length === 0 && view.expertIdentity === null ? (
          <p className="text-on-surface-muted body-2">No participants recorded yet.</p>
        ) : (
          <ul className="flex flex-col gap-12">
            {/*
             * The expert leads the roster (ENG-5730). `participants` is the
             * customer's own approver chain by design and never carries the
             * counterparty, so this card used to list the customer twice and
             * the expert not at all. Composed in here rather than asked of the
             * api, which answers the roster question it was built for.
             */}
            {view.expertIdentity ? (
              <li className="flex items-center gap-12">
                {/* Decorative, matching the approver rows below: `profileId`
                    would add a mouse-only onClick, and the name is the link. */}
                <Avatar
                  fullName={view.expertIdentity.name ?? 'Expert'}
                  src={view.expertIdentity.pictureUrl ?? undefined}
                  sx={{ width: 32, height: 32 }}
                />
                <div className="flex flex-col">
                  <Link
                    to={`/profile/${view.expertIdentity.userId}`}
                    className="text-on-surface body-2"
                  >
                    {view.expertIdentity.name ?? 'The expert'}
                  </Link>
                  <span className="text-on-surface-muted util-caption">Expert</span>
                </div>
              </li>
            ) : null}
            {/*
             * Keyed by person, not by (person, role) — the api sends one row
             * per role and the card used to draw one avatar per role, so
             * whoever held both approver seats read as two people (ENG-5738).
             * `engagementRoster` merges them and orders both the rows and the
             * labels the way the prototype's `partyOrder` does.
             */}
            {roster.map((p) => (
              <li key={p.userId} className="flex items-center gap-12">
                <Avatar fullName={nameOf(p.userId)} sx={{ width: 32, height: 32 }} />
                <div className="flex flex-col">
                  <span className="text-on-surface body-2">{nameOf(p.userId)}</span>
                  <span className="text-on-surface-muted util-caption">{p.roles.join(' · ')}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Activity">
        {view.activity.length === 0 ? (
          <p className="text-on-surface-muted body-2">Nothing has happened yet.</p>
        ) : (
          <ul className="flex flex-col gap-12">
            {view.activity.map((event) => (
              <li key={event.id} className="flex flex-col">
                <span className="text-on-surface body-2">
                  <span className="font-medium">{nameOf(event.actorUserId)}</span>{' '}
                  {activityCopy(event.eventType)}
                </span>
                <span className="text-on-surface-muted body-2">
                  <span>{formatRelative(event.createdAt, now)}</span>
                  {event.reason ? <span>{` · ${event.reason}`}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-on-surface-muted subtitle-2">{label}</dt>
      {/* `whitespace-pre-line` so multi-paragraph scope prose keeps its breaks. */}
      <dd className="mt-4 whitespace-pre-line text-on-surface body-2">{children}</dd>
    </div>
  );
}
